// Home Mode - Chore & Rewards System for PWD Users
// Gamified daily tasks with hard cap of 2 chores per day
// Wellness chores are ALWAYS available (self-care priority)

export interface Chore {
  id: string;
  name: string;
  description: string;
  category: ChoreCategory;
  points: number;
  estimatedMinutes: number;
  difficulty: 'easy' | 'medium' | 'hard';
  icon: string;
  color: string;
  isWellness?: boolean; // Wellness chores bypass daily limit (defaults to false)
}

export type ChoreCategory = 
  | 'tidying'
  | 'kitchen'
  | 'bathroom'
  | 'laundry'
  | 'cleaning'
  | 'outdoor'
  | 'personal'
  | 'wellness';

export interface CompletedChore {
  id: string;
  choreId: string;
  completedAt: string;
  pointsEarned: number;
  date: string; // YYYY-MM-DD for daily tracking
}

export interface Reward {
  id: string;
  name: string;
  description: string;
  pointsCost: number;
  category: RewardCategory;
  icon: string;
  color: string;
  isRecurring: boolean;
}

export type RewardCategory = 'food' | 'activity' | 'outing' | 'privilege' | 'item';

export interface UserProgress {
  totalPoints: number;
  availablePoints: number;
  completedChores: CompletedChore[];
  redeemedRewards: RedeemedReward[];
  currentStreak: number;
  bestStreak: number;
  level: number;
  levelProgress: number;
}

export interface RedeemedReward {
  id: string;
  rewardId: string;
  redeemedAt: string;
  pointsSpent: number;
  notes?: string;
}

// ============================================
// CHORE DEFINITIONS (Hard cap: 2 per day)
// ============================================

export const CHORES: Chore[] = [
  // Tidying
  {
    id: 'tidy-bedroom',
    name: 'Tidy Bedroom',
    description: 'Make bed, put away clothes, clear floor',
    category: 'tidying',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Home',
    color: 'bg-blue-500',
  },
  {
    id: 'tidy-living',
    name: 'Tidy Living Room',
    description: 'Fluff cushions, fold blankets, clear surfaces',
    category: 'tidying',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Home',
    color: 'bg-blue-400',
  },
  {
    id: 'declutter-surface',
    name: 'Declutter One Surface',
    description: 'Clear and wipe one table or counter',
    category: 'tidying',
    points: 40,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Trash2',
    color: 'bg-blue-300',
  },
  
  // Kitchen
  {
    id: 'wash-dishes',
    name: 'Wash Dishes',
    description: 'Load dishwasher or wash by hand',
    category: 'kitchen',
    points: 60,
    estimatedMinutes: 20,
    difficulty: 'medium',
    icon: 'Utensils',
    color: 'bg-green-500',
  },
  {
    id: 'wipe-counters',
    name: 'Wipe Kitchen Counters',
    description: 'Clean all kitchen surfaces',
    category: 'kitchen',
    points: 40,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Sparkles',
    color: 'bg-green-400',
  },
  {
    id: 'clean-sink',
    name: 'Clean Kitchen Sink',
    description: 'Scrub and rinse sink',
    category: 'kitchen',
    points: 50,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Droplet',
    color: 'bg-green-300',
  },
  {
    id: 'sweep-floor',
    name: 'Sweep Kitchen Floor',
    description: 'Sweep and spot mop if needed',
    category: 'kitchen',
    points: 60,
    estimatedMinutes: 15,
    difficulty: 'medium',
    icon: 'Home',
    color: 'bg-green-600',
  },
  
  // Bathroom
  {
    id: 'clean-basin',
    name: 'Clean Bathroom Basin',
    description: 'Scrub sink and wipe mirrors',
    category: 'bathroom',
    points: 50,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Droplet',
    color: 'bg-purple-500',
  },
  {
    id: 'clean-toilet',
    name: 'Clean Toilet',
    description: 'Scrub bowl, wipe seat and exterior',
    category: 'bathroom',
    points: 80,
    estimatedMinutes: 15,
    difficulty: 'medium',
    icon: 'Shield',
    color: 'bg-purple-600',
  },
  {
    id: 'clean-shower',
    name: 'Clean Shower',
    description: 'Spray and wipe shower walls and floor',
    category: 'bathroom',
    points: 100,
    estimatedMinutes: 25,
    difficulty: 'hard',
    icon: 'Droplet',
    color: 'bg-purple-700',
  },
  
  // Laundry
  {
    id: 'load-washer',
    name: 'Load Washing Machine',
    description: 'Sort, load, and start wash',
    category: 'laundry',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Wind',
    color: 'bg-cyan-500',
  },
  {
    id: 'hang-laundry',
    name: 'Hang Laundry',
    description: 'Hang clothes on line or rack',
    category: 'laundry',
    points: 60,
    estimatedMinutes: 20,
    difficulty: 'medium',
    icon: 'Sun',
    color: 'bg-cyan-400',
  },
  {
    id: 'fold-laundry',
    name: 'Fold Clean Laundry',
    description: 'Fold and put away dry clothes',
    category: 'laundry',
    points: 70,
    estimatedMinutes: 25,
    difficulty: 'medium',
    icon: 'Layers',
    color: 'bg-cyan-600',
  },
  
  // Cleaning
  {
    id: 'vacuum-room',
    name: 'Vacuum a Room',
    description: 'Thorough vacuum of one room',
    category: 'cleaning',
    points: 70,
    estimatedMinutes: 20,
    difficulty: 'medium',
    icon: 'Wind',
    color: 'bg-orange-500',
  },
  {
    id: 'mop-floor',
    name: 'Mop Hard Floors',
    description: 'Mop kitchen, bathroom, or living area',
    category: 'cleaning',
    points: 80,
    estimatedMinutes: 25,
    difficulty: 'medium',
    icon: 'Droplet',
    color: 'bg-orange-600',
  },
  {
    id: 'dust-surfaces',
    name: 'Dust Surfaces',
    description: 'Dust furniture and shelves in one room',
    category: 'cleaning',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Wind',
    color: 'bg-orange-400',
  },
  {
    id: 'clean-windows',
    name: 'Clean Windows',
    description: 'Wipe interior windows and sills',
    category: 'cleaning',
    points: 60,
    estimatedMinutes: 20,
    difficulty: 'medium',
    icon: 'Square',
    color: 'bg-orange-300',
  },
  
  // Outdoor
  {
    id: 'water-plants',
    name: 'Water Plants',
    description: 'Water indoor or outdoor plants',
    category: 'outdoor',
    points: 40,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Droplet',
    color: 'bg-emerald-500',
  },
  {
    id: 'sweep-patio',
    name: 'Sweep Patio/Balcony',
    description: 'Sweep outdoor area',
    category: 'outdoor',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Home',
    color: 'bg-emerald-600',
  },
  {
    id: 'pick-up-yard',
    name: 'Pick Up Yard',
    description: 'Collect litter and tidy outdoor space',
    category: 'outdoor',
    points: 60,
    estimatedMinutes: 20,
    difficulty: 'medium',
    icon: 'Trash2',
    color: 'bg-emerald-400',
  },
  
  // Personal
  {
    id: 'make-bed',
    name: 'Make Bed',
    description: 'Straighten sheets and arrange pillows',
    category: 'personal',
    points: 30,
    estimatedMinutes: 5,
    difficulty: 'easy',
    icon: 'Home',
    color: 'bg-pink-500',
  },
  {
    id: 'organize-clothes',
    name: 'Organize Clothes',
    description: 'Put away clean clothes',
    category: 'personal',
    points: 50,
    estimatedMinutes: 15,
    difficulty: 'easy',
    icon: 'Layers',
    color: 'bg-pink-400',
  },
  {
    id: 'pack-bag',
    name: 'Pack Bag for Tomorrow',
    description: 'Prepare bag with essentials',
    category: 'personal',
    points: 40,
    estimatedMinutes: 10,
    difficulty: 'easy',
    icon: 'Backpack',
    color: 'bg-pink-600',
  },
  
  // Wellness
  {
    id: 'morning-routine',
    name: 'Complete Morning Routine',
    description: 'Shower, brush teeth, get dressed',
    category: 'wellness',
    points: 50,
    estimatedMinutes: 30,
    difficulty: 'medium',
    icon: 'Sun',
    color: 'bg-teal-500',
    isWellness: true,
  },
  {
    id: 'evening-routine',
    name: 'Complete Evening Routine',
    description: 'Prepare for bed, hygiene tasks',
    category: 'wellness',
    points: 50,
    estimatedMinutes: 30,
    difficulty: 'medium',
    icon: 'Moon',
    color: 'bg-teal-600',
    isWellness: true,
  },
  {
    id: 'take-medication',
    name: 'Take Medication',
    description: 'Take prescribed medications on time',
    category: 'wellness',
    points: 40,
    estimatedMinutes: 5,
    difficulty: 'easy',
    icon: 'Pill',
    color: 'bg-teal-400',
    isWellness: true,
  },
  {
    id: 'stretch-exercise',
    name: 'Stretch or Exercise',
    description: '10 minutes of movement',
    category: 'wellness',
    points: 60,
    estimatedMinutes: 10,
    difficulty: 'medium',
    icon: 'Activity',
    color: 'bg-teal-700',
    isWellness: true,
  },
];

// ============================================
// REWARD DEFINITIONS
// ============================================

export const REWARDS: Reward[] = [
  // Food Rewards
  {
    id: 'extra-dessert',
    name: 'Extra Dessert Portion',
    description: 'Enjoy an extra serving of your favorite dessert',
    pointsCost: 100,
    category: 'food',
    icon: 'IceCream',
    color: 'bg-pink-500',
    isRecurring: true,
  },
  {
    id: 'favorite-snack',
    name: 'Favorite Snack',
    description: 'Choose a special snack from the shop',
    pointsCost: 80,
    category: 'food',
    icon: 'Cookie',
    color: 'bg-amber-500',
    isRecurring: true,
  },
  {
    id: 'takeaway-meal',
    name: 'Takeaway Meal',
    description: 'Order your favorite takeaway for dinner',
    pointsCost: 300,
    category: 'food',
    icon: 'Pizza',
    color: 'bg-red-500',
    isRecurring: true,
  },
  {
    id: 'smoothie',
    name: 'Special Smoothie',
    description: 'Make or buy a special smoothie',
    pointsCost: 120,
    category: 'food',
    icon: 'CupSoda',
    color: 'bg-purple-500',
    isRecurring: true,
  },
  
  // Activity Rewards
  {
    id: 'movie-night',
    name: 'Movie Night',
    description: 'Choose a movie for family movie night',
    pointsCost: 200,
    category: 'activity',
    icon: 'Film',
    color: 'bg-indigo-500',
    isRecurring: true,
  },
  {
    id: 'game-time',
    name: 'Extra Game Time',
    description: '30 minutes of extra video game time',
    pointsCost: 250,
    category: 'activity',
    icon: 'Gamepad2',
    color: 'bg-violet-500',
    isRecurring: true,
  },
  {
    id: 'choose-music',
    name: 'Choose the Music',
    description: 'Be the DJ for the car or house',
    pointsCost: 150,
    category: 'activity',
    icon: 'Music',
    color: 'bg-fuchsia-500',
    isRecurring: true,
  },
  {
    id: 'art-craft',
    name: 'Art/Craft Time',
    description: 'Special art or craft activity with supplies',
    pointsCost: 280,
    category: 'activity',
    icon: 'Palette',
    color: 'bg-pink-400',
    isRecurring: true,
  },
  
  // Outing Rewards
  {
    id: 'park-visit',
    name: 'Trip to the Park',
    description: 'Visit your favorite local park',
    pointsCost: 250,
    category: 'outing',
    icon: 'Trees',
    color: 'bg-green-500',
    isRecurring: true,
  },
  {
    id: 'beach-trip',
    name: 'Beach Trip',
    description: 'Day trip to the beach',
    pointsCost: 400,
    category: 'outing',
    icon: 'Umbrella',
    color: 'bg-cyan-500',
    isRecurring: true,
  },
  {
    id: 'cafe-visit',
    name: 'Cafe Visit',
    description: 'Go to your favorite cafe for a treat',
    pointsCost: 280,
    category: 'outing',
    icon: 'Coffee',
    color: 'bg-amber-600',
    isRecurring: true,
  },
  {
    id: 'zoo-aquarium',
    name: 'Zoo or Aquarium',
    description: 'Day trip to see the animals',
    pointsCost: 800,
    category: 'outing',
    icon: 'Fish',
    color: 'bg-blue-500',
    isRecurring: false,
  },
  {
    id: 'theme-park',
    name: 'Theme Park Visit',
    description: 'Big day out at a theme park',
    pointsCost: 2000,
    category: 'outing',
    icon: 'Ticket',
    color: 'bg-rose-500',
    isRecurring: false,
  },
  
  // Privilege Rewards
  {
    id: 'choose-dinner',
    name: 'Choose Dinner',
    description: 'Decide what everyone has for dinner',
    pointsCost: 100,
    category: 'privilege',
    icon: 'ChefHat',
    color: 'bg-orange-500',
    isRecurring: true,
  },
  {
    id: 'stay-up-late',
    name: 'Stay Up 30 Min Late',
    description: 'Extra 30 minutes before bedtime',
    pointsCost: 120,
    category: 'privilege',
    icon: 'Moon',
    color: 'bg-indigo-600',
    isRecurring: true,
  },
  {
    id: 'no-chore-pass',
    name: 'No-Chore Pass',
    description: 'Skip one chore tomorrow',
    pointsCost: 200,
    category: 'privilege',
    icon: 'SkipForward',
    color: 'bg-slate-500',
    isRecurring: true,
  },
  {
    id: 'choose-activity',
    name: 'Choose Weekend Activity',
    description: 'Decide what the family does on the weekend',
    pointsCost: 350,
    category: 'privilege',
    icon: 'Star',
    color: 'bg-yellow-500',
    isRecurring: true,
  },
  
  // Item Rewards
  {
    id: 'small-toy',
    name: 'Small Toy/Item',
    description: 'Choose a small item under $20',
    pointsCost: 500,
    category: 'item',
    icon: 'Gift',
    color: 'bg-red-400',
    isRecurring: true,
  },
  {
    id: 'book',
    name: 'New Book',
    description: 'Choose a new book to read',
    pointsCost: 300,
    category: 'item',
    icon: 'Book',
    color: 'bg-blue-400',
    isRecurring: true,
  },
  {
    id: 'clothing-item',
    name: 'Clothing Item',
    description: 'Choose a new clothing item',
    pointsCost: 600,
    category: 'item',
    icon: 'Shirt',
    color: 'bg-purple-400',
    isRecurring: true,
  },
];

// ============================================
// HOME MODE UTILITIES
// ============================================

// Get today's date as YYYY-MM-DD
export function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

// Check if user has completed max chores today (HARD CAP: 2)
export function canCompleteChore(completedChores: CompletedChore[]): boolean {
  const today = getTodayDate();
  const todayChores = completedChores.filter(chore => chore.date === today);
  return todayChores.length < 2;
}

// Get remaining chores for today
export function getRemainingChoreSlots(completedChores: CompletedChore[]): number {
  const today = getTodayDate();
  const todayChores = completedChores.filter(chore => chore.date === today);
  return Math.max(0, 2 - todayChores.length);
}

// Calculate level based on total points (1 level per 500 points)
export function calculateLevel(totalPoints: number): { level: number; progress: number; nextLevel: number } {
  const level = Math.floor(totalPoints / 500) + 1;
  const progress = totalPoints % 500;
  const nextLevel = level * 500;
  return { level, progress, nextLevel };
}

// Calculate streak
export function calculateStreak(completedChores: CompletedChore[]): { current: number; best: number } {
  if (completedChores.length === 0) return { current: 0, best: 0 };
  
  // Group by date
  const dates = [...new Set(completedChores.map(c => c.date))].sort();
  
  let currentStreak = 0;
  let bestStreak = 0;
  let tempStreak = 0;
  
  const today = getTodayDate();
  let expectedDate = today;
  
  // Check current streak (going backwards from today)
  for (let i = dates.length - 1; i >= 0; i--) {
    const date = dates[i];
    if (date === expectedDate) {
      currentStreak++;
      const prevDate = new Date(expectedDate);
      prevDate.setDate(prevDate.getDate() - 1);
      expectedDate = prevDate.toISOString().split('T')[0];
    } else {
      break;
    }
  }
  
  // Calculate best streak
  dates.forEach((date, index) => {
    if (index === 0) {
      tempStreak = 1;
    } else {
      const prevDate = new Date(dates[index - 1]);
      const currDate = new Date(date);
      const diffDays = Math.floor((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        tempStreak++;
      } else {
        bestStreak = Math.max(bestStreak, tempStreak);
        tempStreak = 1;
      }
    }
  });
  
  bestStreak = Math.max(bestStreak, tempStreak);
  
  return { current: currentStreak, best: bestStreak };
}

// Get chores by category
export function getChoresByCategory(category: ChoreCategory): Chore[] {
  return CHORES.filter(chore => chore.category === category);
}

// Get reward by ID
export function getRewardById(id: string): Reward | undefined {
  return REWARDS.find(r => r.id === id);
}

// Get rewards by category
export function getRewardsByCategory(category: RewardCategory): Reward[] {
  return REWARDS.filter(r => r.category === category);
}

// Get affordable rewards
export function getAffordableRewards(availablePoints: number): Reward[] {
  return REWARDS.filter(r => r.pointsCost <= availablePoints);
}
